<template>
  <div class="feature-request-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :checkboxes="true" :search="tableData.search"
                   :enable-simple-search="false"
                   @paramsChanged="getTableData" filter-toggle-string="feature-requests-table"
                   show-toggle-columns :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns">

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="feature-requests-table">
          <feature-request-search/>
        </search-collapse>
      </template>

      <template slot="cell(title)" slot-scope="{item}">
        {{ item.title }}
        <action-buttons :delete-permission="'delete_featurerequest'"
                        :edit-permission="'change_featurerequest'"
                        :view="false"
                        :remove="item.payment_status !== 'paid'"
                        :edit="item.payment_status !== 'paid'"
                        @editClick="onEditClick(item)"
                        @deleteClick="onRemoveClick(item)"/>
      </template>

      <template slot="cell(priority)" slot-scope="{item}">
        {{ item.priority | removeUnderscores | toUpperCaseEveryWord | translate }}
      </template>

      <template slot="cell(module_name)" slot-scope="{item}">
        {{ item.module_name | removeUnderscores | toUpperCaseEveryWord | translate }}
      </template>

      <template slot="cell(status)" slot-scope="{item}">
        <span v-if="item.status" v-as-status="item.status">
          {{ item.status | removeUnderscores | toUpperCaseEveryWord | translate }}
        </span>
      </template>

      <template slot="cell(dev_status)" slot-scope="{item}">
        <span v-if="item.dev_status" v-as-status="item.dev_status">
          {{ item.dev_status | removeUnderscores | toUpperCaseEveryWord | translate}}
        </span>
      </template>

      <template slot="cell(created_at)" slot-scope="{item}">
        {{ item.created_at | toDate }} by {{ item.created_by.display_name }}
      </template>

      <template slot="cell(updated_at)" slot-scope="{item}">
        {{ item.updated_at | toDate }} by {{ item.updated_by.display_name }}
      </template>

      <template slot="cell(description)" slot-scope="{item}">
        {{ item.description | striptags | truncate(50) }}
      </template>

      <template slot="cell(price)" slot-scope="{item}">
        {{ item.price | asCurrency }}
      </template>

      <template slot="cell(payment_status)" slot-scope="{item}">
        <span v-if="item.payment_status" v-as-status="item.payment_status">
          {{ item.payment_status | toUpperCaseEveryWord | removeUnderscores | translate}}
        </span>
      </template>

      <template slot="cell(actions)" slot-scope="{item}">
        <div class="position-relative" v-permission="'change_featurerequest'">
          <b-dropdown right class="text-white" variant="primary" no-caret size="sm">
            <template #button-content>
              <font-awesome-icon icon="ellipsis-v" class="icon"/>
            </template>
            <b-dd-item @click="onReplyClick(item)" v-permission="'change_featurerequest'">
              <font-awesome-icon icon="reply" style="font-size: .9em" class="mr-1"/>
              {{ $t('View Comments') }}
            </b-dd-item>
            <b-dd-item v-if="['invoice_issued', 'invoice_paid'].includes(item.status)" @click="onViewInvoiceClick(item)"
                       v-permission="'change_featurerequest'">
              <font-awesome-icon icon="file-pdf" style="font-size: .9em" class="mr-1"/>
              {{ $t('View Invoice') }}
            </b-dd-item>
          </b-dropdown>
          <b-badge v-if="item && item.total_comments" class="total-comments">
            {{ item.total_comments }}
          </b-badge>
        </div>
      </template>

    </artisio-table>

    <feature-request-modal/>
    <feature-request-comments-modal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import cloneDeep from "lodash/cloneDeep";
  import FeatureRequestSearch from "@/modules/feature-request/FeatureRequestSearch";
  import {FEATURE_REQUEST_TABLE_NAME, SOURCE_FEATURE_REQUEST_TABLE} from "@/modules/constants";
  import FeatureRequestModal from "@/modules/feature-request/FeatureRequestModal";
  import ActionButtons from "@/core/components/ams-table/ActionButtons";
  import FeatureRequestCommentsModal from "@/modules/feature-request/FeatureRequestCommentsModal";
  import FeatureRequestService from "@/modules/feature-request/FeatureRequestService";
  import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";

  const {mapState, mapActions} = createNamespacedHelpers('featureRequest');

  export default {
    name: "FeatureRequestTable",
    components: {
      FeatureRequestCommentsModal,
      ActionButtons, FeatureRequestModal, FeatureRequestSearch, SearchCollapse, ArtisioTable
    },
    data() {
      return {}
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
      }),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(f => f.isVisible);
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: [],
          tableName: `${FEATURE_REQUEST_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'removeFeatureRequest', 'updateToggleColumns', 'showCommentsModal']),
      onEditClick(item) {
        this.showModal({object: item, source: SOURCE_FEATURE_REQUEST_TABLE})
      },
      async onRemoveClick(item) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following feature request?"),
          this.$i18n.t('Feature request was successfully deleted'),
          this.removeFeatureRequest,
          item
        );
      },
      onReplyClick(item) {
        this.showCommentsModal({
          featureRequest: item,
          source: SOURCE_FEATURE_REQUEST_TABLE
        })
      },
      async onPayClick(item) {
        this.tableData.loading = true;
        let res = await FeatureRequestService.pay({uuid: item.uuid});
        this.tableData.loading = false;
        if (!res.success) {
          this.$toast('danger', this.$i18n.t('Feature request not found or already paid, please refresh!'));
        } else {
          window.open(res.body, '_blank');
        }
      },
      async onViewInvoiceClick(item) {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: item.invoice_no}),
          actions: item.status === 'invoice_issued' ? [{
            display_name: this.$i18n.t('Pay Invoice'),
            handler: () => this.onPayClick(item)
          }] : []
        });
        let response = await FeatureRequestService.getInvoicePdfData(item.uuid);
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...response.body,
            filename: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: item.invoice_no})
          })
        } else {
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
          let errorMessage = "";
          if (response.body.non_field_errors) {
            errorMessage = response.body.non_field_errors[0];
          } else {
            errorMessage = response.body;
          }
          this.$errorDialog(errorMessage);
        }
      }
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {
      .actions-col {
        width: 70px;
      }
    }
  }

  .total-comments {
    top: -7px;
    right: 14px;
    margin: 0;
    border-radius: 100%;
    padding: 3px 6px;
    font-size: 10px;
    position: absolute;
    background-color: #1ec973;
    color: white;
  }
</style>

